import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import type { SidebarTab } from "components/Report-generator/components/LeftSidebar";
import { useHistory } from "components/Report-generator/hooks/useHistory";
import { useLastView } from "components/Report-generator/hooks/useLastView";
import { useViewTemplate } from "components/Report-generator/hooks/useViewTemplate";
import {
  buildChartConfig,
  buildKpiConfig,
  buildSummaryConfig,
  buildTableConfig
} from "components/Report-generator/utils/cardDefaults";
import {
  applyPreset,
  cardToLayoutItem,
  compactDownOnly,
  computeMaxRows,
  defaultGridDimensions,
  findFirstAvailablePosition,
  gridWToWidthPercent,
  layoutItemToCardUpdates,
  migrateCardsToGrid
} from "components/Report-generator/utils/gridUtils";
import type { LayoutItem } from "components/Report-generator/utils/gridUtils";
import { getPageMetrics } from "components/Report-generator/utils/pageMetrics";
import { useFormatDateHook } from "hooks";
import { useModals } from "hooks";
import { useReportsActions, useReportsQueriesSelector } from "store";
import type {
  AICardConfig,
  BenchmarkCardConfig,
  CanvasCard,
  CardData,
  CardType,
  ChartCardConfig,
  Currency,
  DataFreshnessCardConfig,
  DataTableCardData,
  DateRange,
  DateRangeValue,
  DateRangeValueType,
  ExposureCardConfig,
  Filters,
  FormatDateUtil,
  KpiCardConfig,
  MacroBriefAvailableIndicator,
  MacroBriefCardConfig,
  PageLayout,
  PerformanceCardConfig,
  ReportMeta,
  ReportOrientation,
  ReportPage,
  SizePreset,
  SummaryCardConfig,
  TableCardConfig,
  View
} from "store/types";
import { DEFAULT_GRID_H, GRID_CONSTANTS, PRESET_GRID_W } from "store/types/reports/report-generator";
import { normalizeGroupByCharacteristic } from "utils";
import toIsoMidday from "utils/to-iso-midday";

const EMPTY_FILTERS: Filters = { investors: [] }; // assetClasses: [] };
const buildDefaultReportMeta = (formatDate: FormatDateUtil): ReportMeta => {
  return {
    title: "Portfolio Report",
    subtitle: "",
    asOfDate: formatDate({ date: toIsoMidday(new Date().toISOString()) }),
    preparedBy: "",
    logo: "",
    background: "",
    backgroundOpacity: 100
  };
};
const DEFAULT_ORIENTATION: ReportOrientation = "portrait";

const cardUid = (): string => {
  return `card-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
};

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

const buildDefaultPages = (count = 1): ReportPage[] => {
  const safeCount = Math.max(count, 1);
  return Array.from({ length: safeCount }, (_, index) => ({
    layout: (index === 0 ? "header-content" : "content") as PageLayout
  }));
};

const getPageCount = (cards: CanvasCard[]): number => {
  return cards.length > 0 ? Math.max(...cards.map((card) => card.page + 1)) : 1;
};

// Dropping item shape for ReactGridLayout
export interface DroppingItem {
  i: string;
  w: number;
  h: number;
}

// ─── Context value ──────────────────────────────────────────────────────────

interface ReportGeneratorContextValue {
  // State
  cards: CanvasCard[];
  selectedCardId: string;
  selectedCard: CanvasCard | null;
  previewMode: boolean;
  isResizing: boolean;
  autoExport: boolean;
  reportMeta: ReportMeta;
  period: DateRangeValue | null;
  dateRange: DateRangeValue | null;
  currency: Currency;
  orientation: ReportOrientation;
  filters: Filters;
  views: View[];
  activeViewId: string | null;
  pages: ReportPage[];
  sidebarTab: SidebarTab;
  batchExportInvestors: string[] | null;
  batchExportProgress: { current: number; total: number; investor: string } | null;
  logoFile: File | null;
  backgroundFile: File | null;
  canUndo: boolean;
  canRedo: boolean;
  previewRef: React.RefObject<HTMLDivElement>;
  macroBriefIndicators: MacroBriefAvailableIndicator[];
  expandedRows: Record<string, Set<string>>;
  handleToggleExpand: (cardId: string, rowKey: string) => void;
  droppingItem: DroppingItem | null;
  settingsOpen: boolean;
  zoom: number;

  // Setters
  setSelectedCardId: (id: string) => void;
  setSettingsOpen: (open: boolean) => void;
  openSettings: (id: string) => void;
  setPreviewMode: (mode: boolean) => void;
  setIsResizing: (resizing: boolean) => void;
  setAutoExport: (auto: boolean) => void;
  setReportMeta: React.Dispatch<React.SetStateAction<ReportMeta>>;
  setCurrency: (currency: Currency) => void;
  setOrientation: (orientation: ReportOrientation) => void;
  setFilters: (filters: Filters) => void;
  setSidebarTab: (tab: SidebarTab) => void;
  setLogoFile: (file: File | null) => void;
  setBackgroundFile: (file: File | null) => void;
  onDateRangeChange: (data: DateRange & { value: DateRangeValueType }) => void;
  setDroppingItem: (item: DroppingItem | null) => void;
  setZoom: (zoom: number) => void;

  // Card actions
  setCards: (update: CanvasCard[] | ((prev: CanvasCard[]) => CanvasCard[])) => void;
  updateCard: (id: string, updates: Partial<Omit<CanvasCard, "id" | "type">>) => void;
  removeCard: (id: string) => void;
  duplicateCard: (id: string) => void;
  autoCleanLayout: () => void;
  onPresetChange: (id: string, preset: SizePreset) => void;

  // Page actions
  addPage: (layout?: PageLayout) => void;
  removePage: (pageIndex: number) => void;
  updatePage: (pageIndex: number, updates: Partial<ReportPage>) => void;

  // View actions
  saveView: (name: string, description?: string) => Promise<void>;
  updateView: (name?: string, description?: string) => Promise<void>;
  loadView: (view: View) => void;
  deleteView: (id: string) => void;

  // Grid handlers
  onLayoutChange: (pageIndex: number, layout: LayoutItem[]) => void;
  onDropCard: (type: CardType, pageIndex: number, gridX: number, gridY: number, resolvedLayout?: LayoutItem[]) => void;
  onLoadLayout: (newCards: CanvasCard[]) => void;
  onClearCanvas: () => void;
  createDefaultCard: (type: CardType, page?: number, dropPosition?: { gridX: number; gridY: number }) => CanvasCard;

  // Undo/redo
  undo: () => void;
  redo: () => void;

  // Export
  handleExportPDF: (ref: React.RefObject<HTMLDivElement | null>, customFilename?: string) => Promise<void>;
  exportPreviewPdf: () => Promise<void>;
  handleStartBatchExport: (investors: string[]) => void;
  handleConfirmBatchExport: () => Promise<void>;
  setBatchExportInvestors: (investors: string[] | null) => void;

  batchCards: CanvasCard[];
  batchPreviewRef: React.RefObject<HTMLDivElement>;
}

const ReportGeneratorContext = createContext<ReportGeneratorContextValue | null>(null);

export const useReportGeneratorContext = (): ReportGeneratorContextValue => {
  const context = useContext(ReportGeneratorContext);
  if (!context) {
    throw new Error("useReportGeneratorContext must be used within a ReportGeneratorProvider");
  }
  return context;
};

// ─── Provider ───────────────────────────────────────────────────────────────

interface ReportGeneratorProviderProps {
  children: React.ReactNode;
}

export const ReportGeneratorProvider: React.FC<ReportGeneratorProviderProps> = ({ children }) => {
  const formatDate = useFormatDateHook();
  const {
    current: cards,
    set: setCards,
    silent: setCardsSilent,
    undo,
    redo,
    canUndo,
    canRedo
  } = useHistory<CanvasCard[]>([]);
  const cardsRef = useRef(cards);
  cardsRef.current = cards;
  const [selectedCardId, setSelectedCardId] = useState("");
  const [previewMode, setPreviewMode] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [autoExport, setAutoExport] = useState(false);
  const [reportMeta, setReportMeta] = useState<ReportMeta>(() => buildDefaultReportMeta(formatDate));
  const [orientation, setOrientation] = useState<ReportOrientation>(DEFAULT_ORIENTATION);
  const [filters, setFilters] = useState<Filters>(cloneJson(EMPTY_FILTERS));
  const [views, setViews] = useState<View[]>([]);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [pages, setPages] = useState<ReportPage[]>(buildDefaultPages());
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>("components");
  const [batchExportInvestors, setBatchExportInvestors] = useState<string[] | null>(null);
  const [batchExportProgress, setBatchExportProgress] = useState<{
    current: number;
    total: number;
    investor: string;
  } | null>(null);
  const [batchCards, setBatchCards] = useState<CanvasCard[]>([]);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [backgroundFile, setBackgroundFile] = useState<File | null>(null);
  const [macroBriefIndicators, setMacroBriefIndicators] = useState<MacroBriefAvailableIndicator[]>([]);
  const [expandedRows, setExpandedRows] = useState<Record<string, Set<string>>>({});
  const [droppingItem, setDroppingItem] = useState<DroppingItem | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [zoom, setZoom] = useState(1);

  const handleSelectCard = useCallback((id: string) => {
    setSelectedCardId(id);
    if (id === "") setSettingsOpen(false);
  }, []);

  const openSettings = useCallback((id: string) => {
    setSelectedCardId(id);
    setSettingsOpen(true);
  }, []);

  const handleToggleExpand = useCallback((cardId: string, rowKey: string) => {
    setExpandedRows((prev) => {
      const cardSet = prev[cardId] ?? new Set<string>();
      const next = new Set(cardSet);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return { ...prev, [cardId]: next };
    });
  }, []);
  const queries = useReportsQueriesSelector();
  const currency = queries.currency as Currency;

  const {
    saveView: saveViewHandler,
    updateView: updateViewHandler,
    loadView: loadViewHandler,
    deleteView: deleteViewHandler
  } = useViewTemplate({
    filters,
    cards,
    period: queries.dateRange,
    currency,
    reportMeta,
    pages,
    orientation,
    logoFile,
    backgroundFile,
    setCards,
    setFilters,
    setLogoFile,
    setBackgroundFile,
    setPeriod: (p) => {
      if (p) setQueriesAction({ dateRange: p });
    },
    setCurrency: (c: Currency) => setQueriesAction({ currency: c }),
    setReportMeta,
    setPages,
    setOrientation,
    setActiveViewId,
    setViews
  });

  const dateRange = queries.dateRange;
  const period = dateRange;

  const { deleteLastView } = useLastView({
    filters,
    cards,
    period,
    currency,
    reportMeta,
    pages,
    orientation,
    logoFile,
    backgroundFile,
    setCards,
    setFilters,
    setLogoFile,
    setBackgroundFile,
    setPeriod: (p) => {
      if (p) setQueriesAction({ dateRange: p });
    },
    setCurrency: (c: Currency) => setQueriesAction({ currency: c }),
    setReportMeta,
    setPages,
    setOrientation
  });

  const previewRef = useRef<HTMLDivElement>(null);
  const selectedCard = cards.find((card) => card.id === selectedCardId) ?? null;

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const ctrl = event.ctrlKey || event.metaKey;
      if (ctrl && event.key === "z" && !event.shiftKey) {
        event.preventDefault();
        undo();
      }
      if (ctrl && (event.key === "y" || (event.key === "z" && event.shiftKey))) {
        event.preventDefault();
        redo();
      }
      if (event.key === "Delete" && selectedCardId) {
        const tag = (event.target as HTMLElement | null)?.tagName;
        if (tag !== "INPUT" && tag !== "TEXTAREA") {
          setCards((prev) => prev.filter((card) => card.id !== selectedCardId));
          handleSelectCard("");
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [redo, selectedCardId, setCards, undo, handleSelectCard]);

  // ── API data fetching ──────────────────────────────────────────────
  const {
    getPieChartAction,
    getReportTableAction,
    getCubicAction,
    getMeasureSummaryAction,
    getUnfundedCommitmentsAction,
    getCashFlowTimelineAction,
    getDataFreshnessAction,
    getPerformanceAttributionAction,
    getMacroBriefIndicatorsAction,
    getMacroBriefAction,
    getMeasureBreakdownAction,
    getIrrBenchmarkAction,
    setQueriesAction,
    askAiAction
  } = useReportsActions();
  const { noticeModal, errorModal } = useModals();
  const setCurrency = useCallback((c: Currency) => setQueriesAction({ currency: c }), [setQueriesAction]);

  // Fetch macro brief indicators on mount
  useEffect(() => {
    let cancelled = false;
    getMacroBriefIndicatorsAction()
      .then((data) => {
        if (!cancelled) setMacroBriefIndicators(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [getMacroBriefIndicatorsAction]);

  const isInTheFuture = (date: string): boolean => {
    const today = new Date();
    today.setHours(23, 59, 59, 998);
    return new Date(date) > today;
  };

  const onDateRangeChange = useCallback(
    (data: DateRange & { value: DateRangeValueType }) => {
      const { to, from } = data;
      if ((to && isInTheFuture(to)) || (from && isInTheFuture(from))) {
        noticeModal({
          msg: "Notice - Report can not show future values of different currency. Different currency's in future dates are ignored."
        });
      }

      if (to && from && from > to) {
        return errorModal({ msg: "'Date From' must be less than 'Date To'" });
      }

      setQueriesAction({
        dateRange: data
      });
    },
    [noticeModal, errorModal, setQueriesAction]
  );

  const createDefaultCard = useCallback(
    (type: CardType, page = 0, dropPosition?: { gridX: number; gridY: number }): CanvasCard => {
      const id = cardUid();
      const { gridW, gridH } = defaultGridDimensions(type);

      const gridX = dropPosition?.gridX ?? 0;
      const gridY = dropPosition?.gridY ?? 0;

      const lastPreset: SizePreset = gridW === PRESET_GRID_W.F ? "F" : gridW === PRESET_GRID_W.S ? "S" : "M";

      const base: CanvasCard = {
        id,
        type,
        gridX,
        gridY,
        gridW,
        gridH,
        widthPercent: gridWToWidthPercent(gridW),
        heightPx: gridH * GRID_CONSTANTS.GRID_CELL_HEIGHT,
        lastPreset,
        rowIndex: 0,
        orderInRow: 0,
        page,
        config: { type: "text", content: "" }
      };

      switch (type) {
        case "chart":
          return { ...base, config: buildChartConfig() };
        case "kpi":
          return { ...base, config: buildKpiConfig() };
        case "summary":
          return { ...base, config: buildSummaryConfig() };
        case "table":
          return { ...base, config: buildTableConfig() };
        case "ai_card":
          return { ...base, config: { type: "ai_card", question: "", answer: "" } as AICardConfig };
        case "text":
          return { ...base, config: { type: "text", content: "" } };
        case "exposure":
          return {
            ...base,
            config: { type: "exposure", dimension: "assetClass", chartType: "donut", measure: "exposure" }
          };
        case "cashflow":
          return { ...base, config: { type: "cashflow" } };
        case "unfunded":
          return { ...base, config: { type: "unfunded" } };
        case "performance":
          return { ...base, config: { type: "performance", sortBy: "irr", topN: 3 } };
        case "benchmark":
          return { ...base, config: { type: "benchmark", benchmarkIds: ["^SPX"] } };
        case "dataFreshness":
          return { ...base, config: { type: "dataFreshness" } };
        case "macroBrief":
          return { ...base, config: { type: "macroBrief", indicators: [] } };
        default:
          return { ...base };
      }
    },
    []
  );

  // Helper to build effective filters for a card (card-specific overrides global)
  const getEffectiveFilters = useCallback(
    (card: CanvasCard): Record<string, string[]> => {
      const cardFilters = card.config.cardFilters ?? EMPTY_FILTERS;

      const merged: Filters = {
        investors: cardFilters.investors.length > 0 ? cardFilters.investors : filters.investors
        // Asset Class hidden:
        // assetClasses: cardFilters.assetClasses.length > 0 ? cardFilters.assetClasses : filters.assetClasses
      };

      const result: Record<string, string[]> = {};

      if (merged.investors.length > 0) {
        result["Investor"] = merged.investors;
      }

      // Asset Class hidden:
      // if (merged.assetClasses.length > 0) {
      //   result["Asset Class"] = merged.assetClasses;
      // }

      return result;
    },
    [filters]
  );

  // Helper to build effective date range / currency for a card (card-specific overrides global)
  const getEffectiveQueries = useCallback(
    (card: CanvasCard) => {
      const cardFilters = card.config.cardFilters;

      return {
        dateRange: cardFilters?.dateRange ?? queries.dateRange,
        currency: cardFilters?.currency ?? queries.currency,
        exchangeMethod: cardFilters?.exchangeMethod ?? queries.exchangeMethod
      };
    },
    [queries]
  );

  const fetchCardData = useCallback(
    async (card: CanvasCard, filterOverride?: Record<string, string[]>): Promise<CardData | undefined> => {
      const cfg = card.config;
      const effectiveFilters = filterOverride ?? getEffectiveFilters(card);
      const {
        dateRange: effectiveDateRange,
        currency: effectiveCurrency,
        exchangeMethod: effectiveExchangeMethod
      } = getEffectiveQueries(card);
      try {
        switch (cfg.type) {
          case "chart": {
            const chartCfg = cfg as ChartCardConfig;
            if (!chartCfg.graphDataFormation) return undefined;
            const chartKey = Object.keys(chartCfg.graphDataFormation)[0];
            const chartConfig = chartCfg.graphDataFormation[chartKey];
            const isScatter = chartConfig.chartType === "scatter";
            if (!isScatter && (!chartConfig.measure || !chartConfig.groupBy)) return undefined;
            if (isScatter && (!chartConfig.xAxis?.name || !chartConfig.yAxis?.name || !chartConfig.groupBy))
              return undefined;
            const isCalcField = !isScatter && chartConfig.measure.startsWith("calc-field-");
            const response = await getPieChartAction({
              filters: effectiveFilters,
              dateRange: effectiveDateRange,
              currency: effectiveCurrency,
              exchangeDate: effectiveExchangeMethod,
              measures: isCalcField ? [] : isScatter ? [] : [chartConfig.measure],
              calculatedFields: isCalcField ? [chartConfig.measure.split("calc-field-")[1]] : [],
              groupBy: [[normalizeGroupByCharacteristic(chartConfig.groupBy)]],
              chartName: chartKey,
              chartType: chartConfig.chartType,
              ...(isScatter && chartConfig.xAxis && { xAxis: chartConfig.xAxis }),
              ...(isScatter && chartConfig.yAxis && { yAxis: chartConfig.yAxis }),
              processKey: `report-generator-${card.id}`,
              pageType: "document"
            });
            return response;
          }
          case "table": {
            const tableCfg = cfg as TableCardConfig;
            if (!tableCfg.columns?.length) return undefined;
            const response = await getReportTableAction({
              dateRange: effectiveDateRange,
              currency: effectiveCurrency,
              exchangeMethod: effectiveExchangeMethod,
              filters: effectiveFilters,
              measures: tableCfg.measures || [],
              calculatedFields: tableCfg.calculatedFields || [],
              characteristics: tableCfg.characteristics || [],
              columns: tableCfg.columns,
              groupBy: tableCfg.groupBy || [],
              columnBy: tableCfg.columnBy || "measure",
              rowBy: tableCfg.rowBy!,
              originalCurrency: false,
              use_accounting_date: queries.use_accounting_date,
              disableDispatch: true,
              processKey: `report-generator-${card.id}`
            });
            const apiColumns = response?.tableData?.columns || [];
            return {
              columns: [
                { key: "name", title: "Name" },
                ...apiColumns.map((col: { key: string; title: string; postfix?: string }, index: number) => ({
                  key: col.key,
                  title: col.title,
                  ...(col.postfix ? { postfix: col.postfix } : {}),
                  aggregation: tableCfg.columns?.[index]?.aggregation,
                  dateRange: tableCfg.columns?.[index]?.dateRange,
                  currency: tableCfg.columns?.[index]?.currency
                }))
              ],
              data: (response?.tableData?.rows || []) as DataTableCardData["data"]
            };
          }
          case "kpi": {
            const kpiCfg = cfg as KpiCardConfig;
            if (!kpiCfg.cubics) return undefined;
            const response = await getCubicAction({
              cubics: kpiCfg.cubics,
              pageType: "document",
              filters: effectiveFilters,
              dateRange: effectiveDateRange,
              currency: effectiveCurrency,
              exchangeMethod: effectiveExchangeMethod,
              use_accounting_date: queries.use_accounting_date,
              processKey: `report-generator-${card.id}`
            });
            return response;
          }
          case "summary": {
            const summaryCfg = cfg as SummaryCardConfig;
            if (!summaryCfg.parameter) return undefined;
            const response = await getMeasureSummaryAction({
              parameter: summaryCfg.parameter,
              filters: effectiveFilters,
              dateRange: effectiveDateRange,
              currency: effectiveCurrency,
              exchangeMethod: effectiveExchangeMethod,
              use_accounting_date: queries.use_accounting_date
            });
            return response;
          }
          case "ai_card": {
            const aiCfg = cfg as AICardConfig;
            if (!aiCfg.question) return undefined;
            const response = await askAiAction({ question: aiCfg.question });
            return { answer: response.answer } as CardData;
          }
          case "unfunded": {
            const response = await getUnfundedCommitmentsAction({
              filters: effectiveFilters,
              dateRange: effectiveDateRange,
              currency: effectiveCurrency,
              exchangeMethod: effectiveExchangeMethod,
              use_accounting_date: queries.use_accounting_date,
              processKey: `report-generator-${card.id}`
            });
            return response;
          }
          case "cashflow": {
            const response = await getCashFlowTimelineAction({
              filters: effectiveFilters,
              dateRange: effectiveDateRange,
              currency: effectiveCurrency,
              exchangeMethod: effectiveExchangeMethod,
              use_accounting_date: queries.use_accounting_date
            });
            return response;
          }
          case "dataFreshness": {
            const freshnessCfg = cfg as DataFreshnessCardConfig;
            const response = await getDataFreshnessAction({ timeRange: freshnessCfg.timeRange ?? "month" });
            return response;
          }
          case "performance": {
            const perfCfg = cfg as PerformanceCardConfig;
            const response = await getPerformanceAttributionAction({
              sortBy: perfCfg.sortBy,
              topN: perfCfg.topN,
              // Ratio measures don't depend on currency; only the date range applies.
              dateRange: effectiveDateRange
            });
            return response;
          }
          case "macroBrief": {
            const macroCfg = cfg as MacroBriefCardConfig;
            if (!macroCfg.indicators.length) return undefined;
            const response = await getMacroBriefAction({
              indicators: macroCfg.indicators,
              include_narrative: !!macroCfg.showNarrative
            });
            return response;
          }
          case "exposure": {
            const expCfg = cfg as ExposureCardConfig;
            const response = await getMeasureBreakdownAction({
              measure: expCfg.measure,
              dimension: expCfg.dimension,
              filters: effectiveFilters,
              dateRange: effectiveDateRange,
              currency: effectiveCurrency,
              exchangeMethod: effectiveExchangeMethod,
              use_accounting_date: queries.use_accounting_date,
              processKey: `report-generator-${card.id}`
            });
            return response;
          }
          case "benchmark": {
            const benchCfg = cfg as BenchmarkCardConfig;
            if (!benchCfg.benchmarkIds.length) return undefined;
            const response = await getIrrBenchmarkAction({
              benchmarks: benchCfg.benchmarkIds,
              filters: effectiveFilters,
              dateRange: effectiveDateRange,
              currency: effectiveCurrency,
              exchangeMethod: effectiveExchangeMethod,
              use_accounting_date: queries.use_accounting_date
            });
            return response;
          }
          default:
            return undefined;
        }
      } catch (error) {
        console.error(`Failed to fetch data for ${cfg.type} card:`, error);
        return undefined;
      }
    },
    [
      getEffectiveFilters,
      getEffectiveQueries,
      queries,
      getPieChartAction,
      getReportTableAction,
      getCubicAction,
      getMeasureSummaryAction,
      getUnfundedCommitmentsAction,
      getCashFlowTimelineAction,
      getDataFreshnessAction,
      getPerformanceAttributionAction,
      getMacroBriefAction,
      getMeasureBreakdownAction,
      getIrrBenchmarkAction,
      askAiAction
    ]
  );

  // Auto-fetch API data when card configs change
  const prevConfigRef = useRef<Record<string, string>>({});
  const prevQueriesRef = useRef<string>("");

  const configFingerprint = useMemo(() => JSON.stringify(cards.map((c) => ({ id: c.id, cfg: c.config }))), [cards]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const currentCards = cardsRef.current;
      for (const card of currentCards) {
        const cfg = card.config;
        let isApiCard = false;
        if (cfg.type === "chart") {
          const chartCfg = cfg as ChartCardConfig;
          const chartData = chartCfg.graphDataFormation?.[Object.keys(chartCfg.graphDataFormation)[0]];
          isApiCard =
            chartData?.chartType === "scatter"
              ? !!(chartData?.xAxis?.name && chartData?.yAxis?.name && chartData?.groupBy)
              : !!(chartData?.measure && chartData?.groupBy);
        } else if (cfg.type === "kpi") {
          isApiCard = !!(cfg as KpiCardConfig).cubics;
        } else if (cfg.type === "summary") {
          isApiCard = !!(cfg as SummaryCardConfig).parameter;
        } else if (cfg.type === "table") {
          isApiCard = !!(cfg as TableCardConfig).columns?.length;
        } else if (cfg.type === "ai_card") {
          isApiCard = !!(cfg as AICardConfig).question;
        } else if (cfg.type === "unfunded") {
          isApiCard = true;
        } else if (cfg.type === "cashflow") {
          isApiCard = true;
        } else if (cfg.type === "dataFreshness") {
          isApiCard = true;
        } else if (cfg.type === "performance") {
          isApiCard = true;
        } else if (cfg.type === "macroBrief") {
          isApiCard = (cfg as MacroBriefCardConfig).indicators.length > 0;
        } else if (cfg.type === "exposure") {
          isApiCard = true;
        } else if (cfg.type === "benchmark") {
          isApiCard = (cfg as BenchmarkCardConfig).benchmarkIds.length > 0;
        }

        if (!isApiCard) continue;

        const fingerprint =
          cfg.type === "exposure"
            ? JSON.stringify({ ...cfg, chartType: undefined })
            : cfg.type === "ai_card"
              ? JSON.stringify({ ...cfg, answer: undefined })
              : cfg.type === "table"
                ? JSON.stringify({
                    ...cfg,
                    columnWidths: undefined,
                    columns: (cfg as TableCardConfig).columns?.map(
                      ({ abbreviated: _abbreviated, decimal: _decimal, ...rest }) => rest
                    )
                  })
                : JSON.stringify(cfg);

        const prevFingerprint = prevConfigRef.current[card.id];

        if (prevFingerprint === fingerprint && card.data) continue;

        if (prevFingerprint !== fingerprint || !card.data) {
          prevConfigRef.current[card.id] = fingerprint;
        }

        if (card.loading) continue;

        setCardsSilent((prev) => prev.map((c) => (c.id === card.id ? { ...c, loading: true } : c)));
        fetchCardData(card)
          .then((data) => {
            setCardsSilent((prev) => prev.map((c) => (c.id === card.id ? { ...c, data, loading: false } : c)));
          })
          .catch(() => {
            setCardsSilent((prev) => prev.map((c) => (c.id === card.id ? { ...c, loading: false } : c)));
          });
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [configFingerprint, fetchCardData]);

  // Re-fetch all API cards when global queries (dateRange, currency, etc.) change
  useEffect(() => {
    const queriesFingerprint = JSON.stringify(queries);
    if (prevQueriesRef.current === queriesFingerprint) return;
    const isInitial = prevQueriesRef.current === "";
    prevQueriesRef.current = queriesFingerprint;
    if (isInitial) return;

    prevConfigRef.current = {};
  }, [queries]);

  // Re-fetch all API cards when filters change
  const prevFiltersRef = useRef<string>("");
  useEffect(() => {
    const filtersFingerprint = JSON.stringify(filters);
    if (prevFiltersRef.current === filtersFingerprint) return;
    const isInitial = prevFiltersRef.current === "";
    prevFiltersRef.current = filtersFingerprint;
    if (isInitial) return;

    prevConfigRef.current = {};
  }, [filters]);

  // ── Card CRUD ────────────────────────────────────────────────────────
  const updateCard = useCallback(
    (id: string, updates: Partial<Omit<CanvasCard, "id" | "type">>) => {
      setCards((prev) =>
        prev.map((card) => {
          if (card.id !== id) return card;
          const updated = { ...card, ...updates };
          // Sync derived fields when grid dimensions change
          if (updates.gridW != null) {
            updated.widthPercent = gridWToWidthPercent(updates.gridW);
          }
          if (updates.gridH != null) {
            updated.heightPx = updates.gridH * GRID_CONSTANTS.GRID_CELL_HEIGHT;
          }
          return updated;
        })
      );
    },
    [setCards]
  );

  const removeCard = useCallback(
    (id: string) => {
      setCards((prev) => prev.filter((card) => card.id !== id));
      setSelectedCardId((current) => {
        if (current === id) {
          setSettingsOpen(false);
          return "";
        }
        return current;
      });
    },
    [setCards]
  );

  const duplicateCard = useCallback(
    (id: string) => {
      setCards((prev) => {
        const source = prev.find((card) => card.id === id);
        if (!source) return prev;

        const pageIdx = source.page;
        const pageLayout = pages[pageIdx]?.layout ?? "content";
        const { height: pageHeight } = getPageMetrics(orientation);
        const maxRows = computeMaxRows(pageHeight, pageLayout);

        // Try to find space on the same page
        const pageCards = prev.filter((c) => c.page === pageIdx);
        const layoutItems = pageCards.map(cardToLayoutItem);
        const pos = findFirstAvailablePosition(
          layoutItems,
          source.gridW,
          source.gridH,
          GRID_CONSTANTS.GRID_COLUMNS,
          maxRows
        );

        const fitsOnPage = pos.y + source.gridH <= maxRows;

        const clone: CanvasCard = {
          ...source,
          id: cardUid(),
          page: pageIdx,
          gridX: fitsOnPage ? pos.x : 0,
          gridY: fitsOnPage ? pos.y : 0,
          rowIndex: 0,
          orderInRow: 0,
          data: undefined,
          loading: false,
          config: cloneJson(source.config)
        };

        if (fitsOnPage) {
          return [...prev, clone];
        }

        // No room — place on a new page
        const newPageIdx = pages.length;
        setPages((p) => [...p, { layout: "content" as PageLayout }]);
        return [...prev, { ...clone, page: newPageIdx }];
      });
    },
    [setCards, setPages, pages, orientation]
  );

  const autoCleanLayout = useCallback(() => {
    setCards((prev) => {
      const cols = GRID_CONSTANTS.GRID_COLUMNS;

      // Pre-pass: auto-size KPI cards based on metric count (≤2 → Medium, 3+ → Full)
      const sized = prev.map((card) => {
        if (card.type !== "kpi" || card.config.type !== "kpi") return card;
        const cubics = (card.config as KpiCardConfig).cubics;
        const metricCount = cubics ? Object.keys(cubics).length : 0;
        if (metricCount === 0) return card;
        const targetW = metricCount <= 2 ? PRESET_GRID_W.M : PRESET_GRID_W.F;
        if (card.gridW === targetW) return card;
        return { ...card, gridW: targetW, lastPreset: (metricCount <= 2 ? "M" : "F") as SizePreset };
      });

      // First pass: group cards into rows and find each row's max height
      const rows: { cards: CanvasCard[]; maxH: number }[] = [];
      let curX = 0;
      let currentRow: CanvasCard[] = [];
      let rowMaxH = 0;

      for (const card of sized) {
        const gridW = card.gridW;
        const gridH = Math.max(card.gridH, DEFAULT_GRID_H[card.type]);

        if (curX + gridW > cols && currentRow.length > 0) {
          rows.push({ cards: currentRow, maxH: rowMaxH });
          currentRow = [];
          curX = 0;
          rowMaxH = 0;
        }

        currentRow.push({ ...card, gridW, gridH });
        curX += gridW;
        rowMaxH = Math.max(rowMaxH, gridH);
      }
      if (currentRow.length > 0) {
        rows.push({ cards: currentRow, maxH: rowMaxH });
      }

      // Second pass: assign positions and equalize row heights
      let curY = 0;
      const result: CanvasCard[] = [];

      for (const row of rows) {
        let x = 0;
        for (const card of row.cards) {
          result.push({
            ...card,
            gridX: x,
            gridY: curY,
            gridH: row.maxH,
            widthPercent: gridWToWidthPercent(card.gridW),
            heightPx: undefined
          });
          x += card.gridW;
        }
        curY += row.maxH;
      }

      return result;
    });
  }, [setCards]);

  const onPresetChange = useCallback(
    (id: string, preset: SizePreset) => {
      setCards((prev) => {
        // 1. Apply the preset size change to the target card
        const updated = prev.map((card) => {
          if (card.id !== id) return card;
          const updates = applyPreset(card, preset);
          return { ...card, ...updates };
        });

        // 2. Resolve collisions on the affected page (no maxRows limit —
        //    overlapping is always worse than overflow).
        const targetCard = updated.find((c) => c.id === id);
        if (!targetCard) return updated;

        const pageIdx = targetCard.page;
        const pageCards = updated.filter((c) => c.page === pageIdx);
        const layout = pageCards.map(cardToLayoutItem);
        const compacted = compactDownOnly(layout);
        const posMap = new Map(compacted.map((item) => [item.i, item]));

        // Apply compacted positions
        let result = updated.map((card) => {
          if (card.page !== pageIdx) return card;
          const pos = posMap.get(card.id);
          if (!pos) return card;
          if (card.gridX === pos.x && card.gridY === pos.y) return card;
          return { ...card, ...layoutItemToCardUpdates(pos) };
        });

        // 3. Move cards that ended up entirely past the page boundary
        //    to a new content page inserted right after the current one.
        const pageLayout = pages[pageIdx]?.layout ?? "content";
        const { height: pageHeight } = getPageMetrics(orientation);
        const maxRows = computeMaxRows(pageHeight, pageLayout);

        const overflowIds = new Set<string>();
        for (const item of compacted) {
          if (item.y >= maxRows) overflowIds.add(item.i);
        }

        if (overflowIds.size > 0) {
          // Insert a new content page after the current one
          const newPageIdx = pageIdx + 1;
          setPages((prev) => {
            const next = [...prev];
            next.splice(newPageIdx, 0, { layout: "content" });
            return next;
          });

          // Find the minimum Y among overflowing items to rebase from 0
          const minOverflowY = compacted
            .filter((item) => overflowIds.has(item.i))
            .reduce((min, item) => Math.min(min, item.y), Infinity);

          result = result.map((card) => {
            // Shift cards on later pages to account for the inserted page
            if (card.page > pageIdx && !overflowIds.has(card.id)) {
              return { ...card, page: card.page + 1 };
            }
            // Move overflowing cards to the new page, rebase Y from 0
            if (overflowIds.has(card.id)) {
              const pos = posMap.get(card.id);
              const newY = (pos ? pos.y : card.gridY) - minOverflowY;
              return {
                ...card,
                page: newPageIdx,
                ...layoutItemToCardUpdates({ i: card.id, x: card.gridX, y: newY, w: card.gridW, h: card.gridH })
              };
            }
            return card;
          });
        }

        return result;
      });
    },
    [setCards, setPages, pages, orientation]
  );

  // ── Page actions ────────────────────────────────────────────────────
  const addPage = useCallback((layout: PageLayout = "content") => {
    setPages((prev) => [...prev, { layout }]);
  }, []);

  const removePage = useCallback(
    (pageIndex: number) => {
      setPages((prev) => prev.filter((_, index) => index !== pageIndex));
      setCards((prev) =>
        prev
          .filter((card) => card.page !== pageIndex)
          .map((card) => (card.page > pageIndex ? { ...card, page: card.page - 1 } : card))
      );
    },
    [setCards]
  );

  const updatePage = useCallback((pageIndex: number, updates: Partial<ReportPage>) => {
    setPages((prev) => prev.map((page, index) => (index === pageIndex ? { ...page, ...updates } : page)));
  }, []);

  // ── View actions ────────────────────────────────────────────────────
  const saveView = useCallback(
    (name: string, description?: string) => saveViewHandler(name, description),
    [saveViewHandler]
  );

  const updateView = useCallback(
    (name?: string, description?: string) => updateViewHandler(name, description),
    [updateViewHandler]
  );

  const loadView = useCallback(
    (view: View) => {
      loadViewHandler(view);
      handleSelectCard("");
    },
    [loadViewHandler, handleSelectCard]
  );

  const deleteView = useCallback(
    (id: string) => {
      void deleteViewHandler(id);
    },
    [deleteViewHandler]
  );

  // ── Grid handlers ──────────────────────────────────────────────────
  const onLayoutChange = useCallback(
    (pageIndex: number, layout: LayoutItem[]) => {
      setCardsSilent((prev) => {
        const layoutMap = new Map(layout.map((item) => [item.i, item]));
        let changed = false;
        const next = prev.map((card) => {
          if (card.page !== pageIndex) return card;
          const item = layoutMap.get(card.id);
          if (!item) return card;
          if (card.gridX === item.x && card.gridY === item.y && card.gridW === item.w && card.gridH === item.h) {
            return card;
          }
          changed = true;
          return { ...card, ...layoutItemToCardUpdates(item) };
        });
        return changed ? next : prev;
      });
    },
    [setCardsSilent]
  );

  const onDropCard = useCallback(
    (type: CardType, pageIndex: number, gridX: number, gridY: number, resolvedLayout?: LayoutItem[]) => {
      const newCard = createDefaultCard(type, pageIndex, { gridX, gridY });
      setCards((prev) => {
        const next = [...prev, newCard];
        if (!resolvedLayout) return next;
        const layoutMap = new Map(resolvedLayout.map((item) => [item.i, item]));
        return next.map((card) => {
          if (card.page !== pageIndex || card.id === newCard.id) return card;
          const item = layoutMap.get(card.id);
          if (!item) return card;
          if (card.gridX === item.x && card.gridY === item.y && card.gridW === item.w && card.gridH === item.h) {
            return card;
          }
          return { ...card, ...layoutItemToCardUpdates(item) };
        });
      });
    },
    [createDefaultCard, setCards]
  );

  const onLoadLayout = useCallback(
    (newCards: CanvasCard[]) => {
      setCards(migrateCardsToGrid(newCards));
      setPages(buildDefaultPages(getPageCount(newCards)));
      handleSelectCard("");
    },
    [setCards, handleSelectCard]
  );

  const onClearCanvas = useCallback(() => {
    setCards([]);
    handleSelectCard("");
    setPages(buildDefaultPages());
    setReportMeta((cur) => ({ ...cur, background: "", backgroundOpacity: 100 }));
    setBackgroundFile(null);
    void deleteLastView();
  }, [setCards, handleSelectCard, setReportMeta, setBackgroundFile, deleteLastView]);

  // ── PDF export ──────────────────────────────────────────────────────
  const handleExportPDF = useCallback(
    async (ref: React.RefObject<HTMLDivElement | null>, customFilename?: string) => {
      if (!ref.current) return;
      try {
        const [{ default: html2canvas }, { default: JsPdf }] = await Promise.all([
          import("html2canvas"),
          import("jspdf")
        ]);
        const { pdfOrientation } = getPageMetrics(orientation);
        const pageDivs = ref.current.querySelectorAll<HTMLDivElement>("[data-pdf-page]");
        const a4Width = pdfOrientation === "landscape" ? 297 : 210;

        const captureCanvas = (el: HTMLElement) => {
          const scrollable = Array.from(el.querySelectorAll<HTMLElement>("*")).filter((node) => {
            const { overflowX, overflowY } = getComputedStyle(node);
            return overflowX === "auto" || overflowX === "scroll" || overflowY === "auto" || overflowY === "scroll";
          });
          const original = scrollable.map((node) => ({
            node,
            overflowX: node.style.overflowX,
            overflowY: node.style.overflowY
          }));
          scrollable.forEach((node) => {
            node.style.overflowX = "visible";
            node.style.overflowY = "visible";
          });
          return html2canvas(el, { scale: 2, useCORS: true, logging: false, backgroundColor: "#fff" }).finally(() => {
            original.forEach(({ node, overflowX, overflowY }) => {
              node.style.overflowX = overflowX;
              node.style.overflowY = overflowY;
            });
          });
        };

        if (pageDivs.length === 0) {
          const canvas = await captureCanvas(ref.current);
          const pageHeightMm = (canvas.height * a4Width) / canvas.width;
          const pdf = new JsPdf({ orientation: pdfOrientation, unit: "mm", format: "a4" });
          pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, a4Width, pageHeightMm);
          const filename = customFilename ?? reportMeta.title;
          pdf.save(`${filename.replace(/[^a-z0-9]/gi, "_")}.pdf`);
          return;
        }

        const canvases: HTMLCanvasElement[] = [];
        for (const page of Array.from(pageDivs)) {
          canvases.push(await captureCanvas(page));
        }

        const pdf = new JsPdf({ orientation: pdfOrientation, unit: "mm", format: "a4" });
        const [firstCanvas] = canvases;
        const firstImgHeight = (firstCanvas.height * a4Width) / firstCanvas.width;
        pdf.addImage(firstCanvas.toDataURL("image/png"), "PNG", 0, 0, a4Width, firstImgHeight);

        for (let index = 1; index < canvases.length; index++) {
          const canvas = canvases[index];
          const imgHeight = (canvas.height * a4Width) / canvas.width;
          pdf.addPage("a4", pdfOrientation);
          pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, a4Width, imgHeight);
        }

        const filename = customFilename ?? reportMeta.title;
        pdf.save(`${filename.replace(/[^a-z0-9]/gi, "_")}.pdf`);
      } catch (error) {
        console.error("PDF export failed:", error);
      }
    },
    [orientation, reportMeta.title]
  );

  const exportPreviewPdf = useCallback(async () => {
    await handleExportPDF(previewRef);
  }, [handleExportPDF]);

  const handleStartBatchExport = useCallback((investors: string[]) => {
    setBatchExportInvestors(investors);
  }, []);

  const batchPreviewRef = useRef<HTMLDivElement>(null);

  const handleConfirmBatchExport = useCallback(async () => {
    const investors = batchExportInvestors;
    if (!investors || investors.length === 0) return;
    setBatchExportInvestors(null);

    for (let i = 0; i < investors.length; i++) {
      const investor = investors[i];
      setBatchExportProgress({ current: i + 1, total: investors.length, investor });
      const investorFilters: Record<string, string[]> = { Investor: [investor] };
      // Asset Class hidden:
      // if (filters.assetClasses.length > 0) {
      //   investorFilters["Asset Class"] = filters.assetClasses;
      // }

      const updatedCards = await Promise.all(
        cardsRef.current.map(async (card) => {
          try {
            const data = await fetchCardData(card, investorFilters);
            return data !== undefined ? { ...card, data, loading: false } : card;
          } catch {
            return card;
          }
        })
      );

      setBatchCards(updatedCards);
      await new Promise((resolve) => setTimeout(resolve, 600));
      const filename = `${reportMeta.title} - ${investor}`;
      await handleExportPDF(batchPreviewRef, filename);
      if (i < investors.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 400));
      }
    }

    setBatchExportProgress(null);
    setBatchCards([]);
  }, [batchExportInvestors, handleExportPDF, reportMeta.title, fetchCardData]);

  // ── Context value ───────────────────────────────────────────────────
  const value: ReportGeneratorContextValue = useMemo(
    () => ({
      cards,
      selectedCardId,
      selectedCard,
      previewMode,
      isResizing,
      autoExport,
      reportMeta,
      period,
      dateRange,
      currency,
      orientation,
      filters,
      views,
      activeViewId,
      pages,
      sidebarTab,
      batchExportInvestors,
      batchExportProgress,
      logoFile,
      backgroundFile,
      canUndo,
      canRedo,
      previewRef,
      macroBriefIndicators,
      expandedRows,
      handleToggleExpand,
      droppingItem,
      settingsOpen,
      zoom,

      setSelectedCardId: handleSelectCard,
      setSettingsOpen,
      openSettings,
      setPreviewMode,
      setIsResizing,
      setAutoExport,
      setReportMeta,
      setCurrency,
      setOrientation,
      setFilters,
      setSidebarTab,
      setLogoFile,
      setBackgroundFile,
      onDateRangeChange,
      setDroppingItem,
      setZoom,

      setCards,
      updateCard,
      removeCard,
      duplicateCard,
      autoCleanLayout,
      onPresetChange,
      addPage,
      removePage,
      updatePage,
      saveView,
      updateView,
      loadView,
      deleteView,
      onLayoutChange,
      onDropCard,
      onLoadLayout,
      onClearCanvas,
      createDefaultCard,

      undo,
      redo,

      handleExportPDF,
      exportPreviewPdf,
      handleStartBatchExport,
      handleConfirmBatchExport,
      setBatchExportInvestors,

      batchCards,
      batchPreviewRef
    }),
    [
      cards,
      selectedCardId,
      selectedCard,
      previewMode,
      isResizing,
      autoExport,
      reportMeta,
      period,
      dateRange,
      currency,
      orientation,
      filters,
      views,
      activeViewId,
      pages,
      sidebarTab,
      batchExportInvestors,
      batchExportProgress,
      logoFile,
      backgroundFile,
      canUndo,
      canRedo,
      macroBriefIndicators,
      expandedRows,
      handleToggleExpand,
      droppingItem,
      settingsOpen,
      zoom,
      onDateRangeChange,
      handleSelectCard,
      openSettings,

      setCurrency,
      setCards,
      updateCard,
      removeCard,
      duplicateCard,
      autoCleanLayout,
      onPresetChange,
      addPage,
      removePage,
      updatePage,
      saveView,
      updateView,
      loadView,
      deleteView,
      onLayoutChange,
      onDropCard,
      onLoadLayout,
      onClearCanvas,
      createDefaultCard,
      undo,
      redo,

      handleExportPDF,
      exportPreviewPdf,
      handleStartBatchExport,
      handleConfirmBatchExport,

      batchCards
    ]
  );

  return <ReportGeneratorContext.Provider value={value}>{children}</ReportGeneratorContext.Provider>;
};
